import { GENERAL_SUPPORT_PHONE_NUMBER, RewardsPrice, Amount } from "redmond";
import {
  getRewardText,
  twoDecimalFormatter,
  getPriceString,
  getCurrencySymbol,
} from "halifax";

// Common
export const READ_TERMS_AND_CONDITIONS = "Read terms and conditions";
export const CANCEL_FOR_ANY_REASON_TITLE = "Cancel for any reason";
export const CANCEL_FOR_ANY_REASON_EXPIRED_TITLE =
  "Your Cancel for any reason has expired";
export const CANCELLATION_INFORMATION_HEADER = "Cancellation information";
export const CONTINUE_BUTTON_COPY = "Continue";
export const CONTINUE_CANCELLATION_BUTTON_COPY = "Continue with cancellation";
export const CONFIRM_CANCELLATION_TITLE = "Confirm cancellation";
export const CONFIRM_CANCELLATION_BUTTON_COPY = "Confirm cancellation";
export const GO_BACK = "Go back";
export const CONFIRM_CANCELLATION_TEXT =
  "Complete the cancellation of your round-trip flight for all travelers.";
export const CONTACT_SUPPORT_COPY = "Contact support";

type CfarCashSubtitleParam = {
  coveragePercent: number | undefined;
  cash: Amount | undefined;
  travelCredit: Amount | undefined;
  reward: RewardsPrice | undefined;
  copyVariant: "amount" | "percent";
};

export const getCfarRefundAmountTextWithTravelCredit = ({
  cash,
  reward,
  travelCredit,
}: {
  cash: Amount | undefined;
  reward: RewardsPrice | undefined;
  travelCredit: Amount | undefined;
}) => {
  const cashText =
    cash != null && cash.amount !== 0
      ? getPriceString({
          price: cash.amount,
          currencySymbol: getCurrencySymbol(cash.currency),
          priceFormatter: twoDecimalFormatter,
        })
      : "";

  const travelCreditText =
    travelCredit != null && travelCredit.amount !== 0
      ? `${getPriceString({
          price: travelCredit.amount,
          currencySymbol: getCurrencySymbol(travelCredit.currency),
          priceFormatter: twoDecimalFormatter,
        })} in travel credit`
      : "";

  const rewardsText =
    reward != null && reward.value !== 0
      ? getRewardText({
          reward,
        })
      : "";

  const refunds = [rewardsText, travelCreditText, cashText].filter((r) => !!r);

  switch (refunds.length) {
    case 0:
      return undefined;
    case 1:
      return refunds[0];
    case 2:
      return refunds.join(" and ");
    case 3:
      return `${refunds.slice(0, -1).join(", ")} and ${
        refunds[refunds.length - 1]
      }`;
    default:
      return undefined;
  }
};

// CfarRefund
export const getCfarCashSubtitle = (
  cfarCashSubtitleParam: CfarCashSubtitleParam
) => {
  const { cash, reward, travelCredit, coveragePercent, copyVariant } =
    cfarCashSubtitleParam;
  const cfarAmountText = getCfarRefundAmountTextWithTravelCredit({
    cash,
    reward,
    travelCredit,
  });

  if (cfarAmountText == null) {
    return "You'll receive a refund back to your original payment method.";
  }

  switch (copyVariant) {
    case "percent":
      return `You'll receive a <strong>${
        coveragePercent ?? 100
      }% refund (${cfarAmountText})</strong> of your hotel base rate and taxes back to your original payment method.`;
    case "amount":
    default:
      return `You'll receive <strong>a refund of ${cfarAmountText}</strong>${
        cash?.amount != null && cash.amount > 0
          ? " back to your original payment method"
          : ""
      }.`;
  }
};

export const getCfarRefundAmountSubtitle = ({
  cash,
  reward,
  travelCredit,
}: {
  cash: Amount | undefined;
  reward: RewardsPrice | undefined;
  travelCredit: Amount | undefined;
}) => {
  const cfarAmountText = getCfarRefundAmountTextWithTravelCredit({
    cash,
    reward,
    travelCredit,
  });

  if (!cfarAmountText) {
    return "You'll receive a refund back to your original payment method. You should receive your refund within the next 1-2 billing cycles, but in some cases it may take longer.";
  }

  return `You'll receive a refund of <strong>${cfarAmountText}</strong>${
    cash?.amount && cash.amount > 0 ? " to your original payment method" : ""
  }. You should receive your refund within the next 1-2 billing cycles, but in some cases it may take longer.`;
};

export const getCfarRefundAmountText = ({
  cash,
  reward,
}: {
  cash: Amount | undefined;
  reward: RewardsPrice | undefined;
}) => {
  const cashText =
    cash != null && cash.amount !== 0
      ? getPriceString({
          price: cash.amount,
          currencySymbol: getCurrencySymbol(cash.currency),
          priceFormatter: twoDecimalFormatter,
        })
      : "";

  const rewardsText =
    reward != null && reward.value !== 0
      ? getRewardText({
          reward,
        })
      : "";

  if (!cashText && !rewardsText) {
    return "";
  }

  if (!cashText) {
    return rewardsText;
  }

  if (!rewardsText) {
    return cashText;
  }

  return `${cashText} and ${rewardsText}`;
};

export const getCfarConfirmInformation = (expiry: string | undefined) => [
  `You must cancel your booking before ${
    expiry || " the check-in time on your reservation"
  }.`,
  "Cancellations cannot be reversed.",
  "Refunds will be issued using the same method of payment as the original booking.",
  "Any travel offers are not included.",
  `If you have any questions about canceling your booking, please contact us at ${GENERAL_SUPPORT_PHONE_NUMBER.boldText}.`,
];

export const COMPLETE_CANCELLATION_SUBTITLE = `Complete the cancellation of your booking.`;

// Cancellation Pending
export const CLOSE_BUTTON_COPY = "Close";
export const PENDING_CANCELLATION_TITLE = "We're processing your confirmation";
export const PENDING_CANCELLATION_SUBTITLE =
  "Your booking has already been cancelled. A confirmation email will be sent and My trips will be updated within 1-2 hours.";

/*
  note: DIALOG_MARGIN is the same as the margin in MuiDialog-paperScrollPaper, which comes from material-ui.Dialog;
  MuiDialog-paperScrollPaper has max-height set to calc(100% - 2 * 32px).
*/
export const DIALOG_MARGIN = 32;

export const getPartialScrollThreshold = () => {
  const height = 750;
  return `(max-height: ${height + 2 * DIALOG_MARGIN}px)`;
};
